import { useEffect, useRef, useState } from "react";

import { Picture } from "@/components/media/Picture";

/**
 * Decorative background video loop layered over a still poster photo.
 *
 * The poster paints immediately; the self-hosted video element is only mounted
 * once the page is idle AND the section is near the viewport, so it never
 * competes with first load. Hidden from assistive tech and the tab order, and
 * skipped entirely under reduced motion or on small screens.
 *
 * Served from our own origin (no third-party player), so it sets no
 * third-party cookies and ships no vendor JavaScript.
 */
export function BackgroundVideo({
  src,
  poster,
  title,
}: {
  src: string;
  poster: string;
  title: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    if (window.matchMedia("(max-width: 767px)").matches) return undefined;

    let observer: IntersectionObserver | undefined;
    let idle: number | undefined;

    const start = () => {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setMounted(true);
            observer?.disconnect();
          }
        },
        { rootMargin: "100% 0px" },
      );
      observer.observe(el);
    };

    const idleCallback = (window as Window & { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    if (idleCallback) idle = idleCallback(start);
    else idle = window.setTimeout(start, 1200);

    return () => {
      observer?.disconnect();
      if (idle !== undefined) window.clearTimeout(idle);
    };
  }, []);

  // Only reveal the video once it is actually playing; if it fails to load the
  // poster simply stays in place.
  useEffect(() => {
    if (!mounted) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    const play = video.play();
    if (play && typeof play.catch === "function") play.catch(() => setVisible(false));
    return undefined;
  }, [mounted]);

  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden" aria-hidden>
      <Picture
        src={poster}
        alt=""
        sizes="100vw"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />

      {mounted ? (
        <video
          ref={videoRef}
          aria-label={title}
          muted
          loop
          playsInline
          autoPlay
          preload="none"
          tabIndex={-1}
          onPlaying={() => setVisible(true)}
          onError={() => setVisible(false)}
          className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
            visible ? "opacity-100" : "opacity-0"
          }`}
        >
          <source src={src} type="video/mp4" />
          <source src={src.replace(/\.mp4$/, ".webm")} type="video/webm" />
        </video>
      ) : null}
    </div>
  );
}
